"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	EmptyState,
	GlassTabPill,
	GlassTextField,
} from "@/components/design-system/ArtistDesignSystem";
import {
	browserDiscoveryRepository,
	type DiscoveryRepository,
	type DiscoverySearchResult,
} from "@/domain/discovery/DiscoveryRepository";
import { AppleMusicConnectState } from "./AppleMusicConnectState";
import { SearchResultsList } from "./SearchResultsList";
import { SearchSkeleton } from "./SearchSkeleton";

type SearchScope = "artist" | "appleMusic";

type SearchPhase = "idle" | "searching" | "ready" | "error";

const SEARCH_SCOPES: readonly SearchScope[] = ["artist", "appleMusic"];
const SCOPE_LABEL: Readonly<Record<SearchScope, string>> = {
	artist: "Artist",
	appleMusic: "Apple Music",
};
const DEBOUNCE_MS = 500;
const EMPTY_RESULT: DiscoverySearchResult = {
	tracks: [],
	playlists: [],
	inspirations: [],
};

/**
 * SearchExperience — full search page matching iOS `SearchView`.
 *
 * Layout: `GlassTextField` (leading search glyph, auto-focus, submit on
 * Enter) → `GlassTabPill` (Artist / Apple Music scopes) → conditional body:
 *
 * - Empty query + Artist scope → `EmptyState` (search glyph + "搜索音乐")
 * - Empty query + Apple Music scope → `AppleMusicConnectState` (permissionDenied)
 * - Searching → `SearchSkeleton` (6 shimmer rows in a `GlassCard`)
 * - Error → `EmptyState` (warning glyph + retry)
 * - No results → `EmptyState` (music glyph + "没有结果")
 * - Results → `SearchResultsList` (`GlassCard(padding=0)` + `MediaRow` list)
 *
 * Search is debounced 500ms (matching iOS) and re-runs on scope change.
 */
export function SearchExperience({
	repository = browserDiscoveryRepository,
}: Readonly<{ repository?: DiscoveryRepository }>) {
	const auth = useArtistSession();
	const [scope, setScope] = useState<SearchScope>("artist");
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [phase, setPhase] = useState<SearchPhase>("idle");
	const [results, setResults] = useState<DiscoverySearchResult>(EMPTY_RESULT);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const requestIdRef = useRef(0);

	// Debounce the query (500ms, matching iOS).
	useEffect(() => {
		const trimmed = query.trim();
		const timer = window.setTimeout(
			() => setDebouncedQuery(trimmed),
			DEBOUNCE_MS,
		);
		return () => window.clearTimeout(timer);
	}, [query]);

	// Run the search whenever the debounced query or scope changes.
	useEffect(() => {
		if (debouncedQuery.length === 0) {
			setPhase("idle");
			setResults(EMPTY_RESULT);
			setErrorMessage(null);
			return;
		}
		// Apple Music scope on web is always permission-denied — no search.
		if (scope === "appleMusic") {
			setPhase("idle");
			setResults(EMPTY_RESULT);
			return;
		}
		let active = true;
		const currentRequestId = ++requestIdRef.current;
		setPhase("searching");
		setErrorMessage(null);
		repository
			.searchArtist(debouncedQuery, auth.scope)
			.then((value) => {
				if (!active || currentRequestId !== requestIdRef.current) return;
				setResults(value);
				setPhase("ready");
			})
			.catch(() => {
				if (!active || currentRequestId !== requestIdRef.current) return;
				setResults(EMPTY_RESULT);
				setPhase("error");
				setErrorMessage("搜索暂时失败，请检查网络后重试。");
			});
		return () => {
			active = false;
		};
	}, [auth.scope, debouncedQuery, repository, scope]);

	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const trimmed = query.trim();
			if (trimmed.length === 0) return;
			// Force immediate search by flushing the debounce.
			setDebouncedQuery(trimmed);
		},
		[query],
	);

	const handleScopeChange = useCallback((next: SearchScope) => {
		setScope(next);
	}, []);

	const handleRetry = useCallback(() => {
		if (debouncedQuery.length === 0) return;
		const currentRequestId = ++requestIdRef.current;
		setPhase("searching");
		setErrorMessage(null);
		repository
			.searchArtist(debouncedQuery, auth.scope)
			.then((value) => {
				if (currentRequestId !== requestIdRef.current) return;
				setResults(value);
				setPhase("ready");
			})
			.catch(() => {
				if (currentRequestId !== requestIdRef.current) return;
				setResults(EMPTY_RESULT);
				setPhase("error");
				setErrorMessage("搜索暂时失败，请检查网络后重试。");
			});
	}, [auth.scope, debouncedQuery, repository]);

	const handleAppleMusicConnect = useCallback(() => {
		if (typeof window === "undefined") return;
		window.open("https://music.apple.com", "_blank", "noopener,noreferrer");
	}, []);

	const totalResults = useMemo(
		() =>
			results.tracks.length +
			results.playlists.length +
			results.inspirations.length,
		[results],
	);

	const body = renderBody({
		scope,
		debouncedQuery,
		phase,
		totalResults,
		errorMessage,
		results,
		onRetry: handleRetry,
		onAppleMusicConnect: handleAppleMusicConnect,
	});

	return (
		<section className="search-experience" aria-labelledby="search-heading">
			<h1 id="search-heading" className="search-experience__title">
				{`搜索 ${SCOPE_LABEL[scope]}`}
			</h1>
			{/* biome-ignore lint/a11y/useSemanticElements: form element is required for submit handling; role="search" provides the search landmark semantics */}
		<form
			className="search-experience__form"
			onSubmit={handleSubmit}
			role="search"
		>
				<GlassTextField
					label="搜索"
					type="search"
					placeholder="搜索歌曲、艺人"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					leading={<ArtistIcon name="search" size={20} />}
					autoFocus
					autoComplete="off"
					className="search-experience__field"
				/>
			</form>
			<GlassTabPill<SearchScope>
				tabs={SEARCH_SCOPES}
				selected={scope}
				onSelect={handleScopeChange}
				label={(value) => SCOPE_LABEL[value]}
				className="search-experience__scopes"
			/>
			<div className="search-experience__body">{body}</div>
		</section>
	);
}

function renderBody({
	scope,
	debouncedQuery,
	phase,
	totalResults,
	errorMessage,
	results,
	onRetry,
	onAppleMusicConnect,
}: Readonly<{
	scope: SearchScope;
	debouncedQuery: string;
	phase: SearchPhase;
	totalResults: number;
	errorMessage: string | null;
	results: DiscoverySearchResult;
	onRetry: () => void;
	onAppleMusicConnect: () => void;
}>) {
	// Apple Music scope on web is always permission-denied.
	if (scope === "appleMusic") {
		return <AppleMusicConnectState onConnect={onAppleMusicConnect} />;
	}
	// Empty query → empty state.
	if (debouncedQuery.length === 0) {
		return (
			<EmptyState
				symbol={<ArtistIcon name="search" size={38} />}
				title="搜索音乐"
				message="搜索你喜欢的歌曲和艺人。"
			/>
		);
	}
	// Searching → skeleton.
	if (phase === "searching") {
		return <SearchSkeleton />;
	}
	// Error → retry state.
	if (phase === "error") {
		return (
			<EmptyState
				symbol={<ArtistIcon name="sparkle" size={38} />}
				title="搜索失败"
				message={errorMessage ?? "搜索暂时失败，请稍后重试。"}
				actionLabel="重试"
				actionSymbol={<ArtistIcon name="sparkle" size={16} />}
				onAction={onRetry}
			/>
		);
	}
	// No results.
	if (phase === "ready" && totalResults === 0) {
		return (
			<EmptyState
				symbol={<ArtistIcon name="music" size={38} />}
				title="没有结果"
				message={`没有找到“${debouncedQuery}”。尝试其他关键词。`}
			/>
		);
	}
	// Results — iOS shows only the track list.
	return <SearchResultsList tracks={results.tracks} />;
}
